import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { describeApiError } from '../api/apiClient';
import { useAuditRunStatusQuery } from '../api/audit-run-status-queries';

function formatTime(iso: string | null): string {
  if (!iso) return 'never recorded';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * App-wide banner that makes audit failures visible:
 * - last run failed → "Last audit failed at <time>: <reason>" + last successful run
 * - no success within the expected window (status may not have been writable)
 * - status endpoint itself unreachable (storage down)
 * Renders nothing when the last run succeeded on schedule.
 */
export default function AuditRunStatusBanner() {
  const { data, error } = useAuditRunStatusQuery();

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Audit run status is unavailable — {describeApiError(error)}. Dashboard data may be out of
        date.
      </Alert>
    );
  }
  if (!data) return null;

  if (data.lastStatus === 'failed') {
    return (
      <Alert severity="error" sx={{ mb: 2 }} role="alert">
        <AlertTitle>
          Last audit failed at {formatTime(data.lastFailureAt ?? data.lastRunAt)}:{' '}
          {data.lastError || 'unknown error'}
        </AlertTitle>
        Last successful run: {formatTime(data.lastSuccessAt)}. Dashboard data is from that run.
        {data.lastTrigger === 'scheduled' ? ' (scheduled run)' : ' (manual run)'}
      </Alert>
    );
  }

  const violations = data.integrity?.issueInvariantViolations ?? 0;
  if (violations > 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} role="alert">
        The last audit found {violations} issue{violations === 1 ? '' : 's'} that contradict the
        device records. Issue counts may be wrong until this is fixed.
      </Alert>
    );
  }

  if (data.stale) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No successful audit in the last {data.staleAfterHours} hours. Last successful run:{' '}
        {formatTime(data.lastSuccessAt)}.
      </Alert>
    );
  }

  return null;
}
